import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Global Equine Group";
const DEFAULT_TITLE = "Global Equine Group – A Unique Racing Management Company";
const DEFAULT_DESCRIPTION =
  "Global Equine Group is a unique racing and bloodstock management company based in Newmarket, England, managing racehorses for clients across 6 continents with a bespoke, transparent, data-driven approach.";
const DEFAULT_IMAGE = "/img/logo.webp";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function Seo({ title, description, image, type = "website", jsonLd = [] }) {
  const { pathname } = useLocation();
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const origin = window.location.origin;
    const rawImg = image || DEFAULT_IMAGE;
    const img = rawImg.startsWith("http") ? rawImg : `${origin}${rawImg}`;
    const url = `${origin}${pathname}`;

    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // JSON-LD structured data (Organization + WebSite on every page, plus page-specific)
    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove());
    const base = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: origin,
        logo: `${origin}${DEFAULT_IMAGE}`,
        description: DEFAULT_DESCRIPTION,
        email: "info@globalequinegroup.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Newmarket",
          addressRegion: "Suffolk",
          addressCountry: "GB",
        },
        sameAs: [
          "https://www.instagram.com/global_equine_group/",
          "https://x.com/globalequine",
          "https://www.linkedin.com/company/global-equine-group-ltd/",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/blog?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ];
    [...base, ...JSON.parse(jsonLdKey)].forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, image, type, pathname, jsonLdKey]);

  return null;
}
